/**
 * ResidentNotifications — full feed with read tracking.
 */
import { useCallback, useEffect, useState } from 'react';
import { CheckCheck, Bell } from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';

const GOLD = '#C9A962';
const CAT_LABELS = {
  service_scheduled: 'Service Scheduled', service_completed: 'Service Completed',
  service_delayed: 'Service Delayed', violation_update: 'Violation Update',
  announcement: 'Announcement',
};

export default function ResidentNotifications({ onChanged }) {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch('/api/resident/notifications');
      setRows(data.notifications || []);
    } catch (_) { setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await apiFetch(`/api/resident/notifications/${id}/read`, { method: 'POST' });
    load(); onChanged?.();
  };
  const markAll = async () => {
    await apiFetch('/api/resident/notifications/read-all', { method: 'POST' });
    load(); onChanged?.();
  };

  if (rows === null) return null;

  return (
    <div data-testid="resident-notifications-screen">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-black uppercase tracking-[0.25em]" style={{ color: GOLD }}>Notifications</h2>
        {rows.some((r) => !r.read) && (
          <button onClick={markAll} className="flex items-center gap-1 text-[11px] font-bold" style={{ color: GOLD }} data-testid="resident-mark-all-read">
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </button>
        )}
      </div>
      {rows.length === 0 && (
        <div className="rounded-xl border border-zinc-800 p-8 text-center">
          <Bell className="h-6 w-6 mx-auto text-zinc-700 mb-2" />
          <p className="text-xs text-zinc-600">No notifications yet. Service updates and alerts for your community will appear here.</p>
        </div>
      )}
      <div className="space-y-2">
        {rows.map((n) => (
          <button key={n.notif_id} onClick={() => !n.read && markRead(n.notif_id)}
            className={`w-full text-left rounded-xl border p-3.5 ${n.read ? 'border-zinc-800 bg-zinc-900/40' : 'border-[#C9A962]/50 bg-[#C9A962]/5'}`}
            data-testid={`resident-notif-${n.notif_id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-[9px] font-black uppercase tracking-wider" style={{ color: GOLD }}>
                {CAT_LABELS[n.category] || 'Update'}
              </span>
              {!n.read && <span className="h-2 w-2 rounded-full bg-red-600 shrink-0" />}
            </div>
            <p className="text-sm font-bold text-white mt-1.5 break-words">{n.title}</p>
            <p className="text-[11px] text-zinc-400 mt-0.5 break-words">{n.message}</p>
            <p className="text-[10px] text-zinc-600 mt-1">{new Date(n.created_at).toLocaleString()}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
